import { Button } from '@heroui/react'
import { Check, Eye, EyeOff, Lock, LogOut, ShieldAlert } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../context/AuthContext'
import { forceChangeOwnPassword } from '../services/api'

export function ForceChangePasswordModal() {
  const { user, updateUser, logout } = useAuth()
  const navigate = useNavigate()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Render modal ONLY if user is authenticated and mustChangePassword is true
  if (!user || !user.mustChangePassword) {
    return null
  }

  const isMinLength = newPassword.length >= 10
  const isMatch = newPassword.length > 0 && newPassword === confirmPassword
  const isValid = isMinLength && isMatch

  const handleSignOut = () => {
    logout()
    navigate('/login')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!isValid) return

    setSaving(true)
    setError('')

    try {
      await forceChangeOwnPassword(newPassword)
      // Update session state so modal disappears immediately
      updateUser({ mustChangePassword: false })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Gagal memperbarui kata sandi. Silakan coba lagi.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-change-password-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200">
        {/* Header with Icon */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 ring-8 ring-amber-50 dark:ring-amber-950/20">
            <ShieldAlert size={28} />
          </div>
          <h2 id="force-change-password-title" className="text-xl font-bold text-slate-900 dark:text-white">
            Wajib Ganti Kata Sandi
          </h2>
          <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-400 max-w-sm">
            Kata sandi akun Anda baru saja direset oleh administrator. Demi keamanan akun, Anda diwajibkan untuk membuat kata sandi baru pribadi Anda sebelum dapat melanjutkan.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-600 dark:bg-red-950/40 dark:text-red-300 border border-red-200 dark:border-red-900/50">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="erp-form-label flex items-center justify-between">
              <span>Kata Sandi Baru</span>
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center gap-1 cursor-pointer"
                onClick={() => setShowPassword(prev => !prev)}
              >
                {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                {showPassword ? 'Sembunyikan' : 'Lihat'}
              </button>
            </label>
            <div className="relative">
              <input
                data-testid="force-change-new-password-input"
                type={showPassword ? 'text' : 'password'}
                className="wicend-input pr-10"
                placeholder="Minimal 10 karakter"
                required
                minLength={10}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
              <Lock size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="erp-form-label">Konfirmasi Kata Sandi Baru</label>
            <div className="relative">
              <input
                data-testid="force-change-confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                className="wicend-input pr-10"
                placeholder="Ulangi kata sandi baru"
                required
                minLength={10}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Lock size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Validation Checklist */}
          <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50 space-y-1.5 text-xs">
            <div className={`flex items-center gap-2 ${isMinLength ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-slate-400'}`}>
              <div className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${isMinLength ? 'bg-emerald-100 dark:bg-emerald-950/60' : 'bg-slate-200 dark:bg-slate-700'}`}>
                {isMinLength ? <Check size={11} /> : '•'}
              </div>
              <span>Minimal 10 karakter</span>
            </div>
            <div className={`flex items-center gap-2 ${isMatch ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-slate-400'}`}>
              <div className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${isMatch ? 'bg-emerald-100 dark:bg-emerald-950/60' : 'bg-slate-200 dark:bg-slate-700'}`}>
                {isMatch ? <Check size={11} /> : '•'}
              </div>
              <span>Konfirmasi kata sandi cocok</span>
            </div>
          </div>

          <div className="pt-2 space-y-2">
            <Button
              data-testid="force-change-submit-btn"
              type="submit"
              variant="primary"
              className="w-full h-11 text-sm font-semibold justify-center"
              isDisabled={saving || !isValid}
            >
              {saving ? 'Menyimpan Kata Sandi Baru...' : 'Simpan Kata Sandi Baru'}
            </Button>
            <Button
              data-testid="force-change-logout-btn"
              type="button"
              variant="ghost"
              className="w-full h-10 text-xs text-slate-500 hover:text-red-600 justify-center"
              onPress={handleSignOut}
            >
              <div className="flex items-center gap-1.5">
                <LogOut size={14} /> Keluar dari Akun
              </div>
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
